import { useState } from "react";
import api from "@/lib/api";
import { Users, ArrowRight } from "lucide-react";

function timeToSeconds(raw) {
  if (!raw) return null;
  const s = String(raw).trim().replace(",", ".");
  const m = s.match(/^(?:(\d+):)?(\d{1,2})(?:\.(\d{1,3}))?$/);
  if (m) {
    const min = parseInt(m[1] || "0", 10);
    const sec = parseInt(m[2] || "0", 10);
    const ms = m[3] ? parseInt(m[3].padEnd(3, "0"), 10) : 0;
    return min * 60 + sec + ms / 1000;
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={onChange} data-testid={testid}
    className="w-full bg-zinc-950 border border-zinc-700 focus:border-yellow-400 outline-none px-3 py-3 text-zinc-100 text-sm transition-colors duration-200">
    <option value="">Seleziona pilota…</option>
    {options.map((n) => <option key={n} value={n}>{n}</option>)}
  </select>
);

export default function PilotCompare({ champId, drivers }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);

  const load = (name, setData) => {
    if (!name) { setData(null); return; }
    api.get(`/championships/${champId}/pilots/${encodeURIComponent(name)}`).then((r) => setData(r.data)).catch(() => setData(null));
  };

  const rows = [
    { key: "points", label: "Punti", get: (d) => d.totals?.points || 0, better: "high" },
    { key: "races", label: "Gare", get: (d) => d.totals?.races || 0, better: "high" },
    { key: "wins", label: "Vittorie", get: (d) => d.totals?.wins || 0, better: "high" },
    { key: "podiums", label: "Podi", get: (d) => d.totals?.podiums || 0, better: "high" },
    { key: "best_position", label: "Miglior posizione", get: (d) => d.best_position ? `P${d.best_position}` : "—", raw: (d) => d.best_position, better: "low" },
    { key: "best_time", label: "Miglior tempo", get: (d) => d.best_time || "—", raw: (d) => timeToSeconds(d.best_time), better: "low" },
  ];

  const winner = (row) => {
    if (!dataA || !dataB) return null;
    const va = row.raw ? row.raw(dataA) : row.get(dataA);
    const vb = row.raw ? row.raw(dataB) : row.get(dataB);
    if (va == null && vb == null) return null;
    if (va == null) return "b";
    if (vb == null) return "a";
    if (va === vb) return null;
    if (row.better === "high") return va > vb ? "a" : "b";
    return va < vb ? "a" : "b";
  };

  return (
    <div data-testid="pilot-compare">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-center mb-8">
        <Select value={a} testid="compare-select-a" options={drivers.filter((n) => n !== b)}
          onChange={(e) => { setA(e.target.value); load(e.target.value, setDataA); }} />
        <div className="hidden sm:flex items-center justify-center text-zinc-600">
          <Users className="h-5 w-5" />
        </div>
        <Select value={b} testid="compare-select-b" options={drivers.filter((n) => n !== a)}
          onChange={(e) => { setB(e.target.value); load(e.target.value, setDataB); }} />
      </div>

      {!dataA || !dataB ? (
        <p className="text-zinc-500 py-10 text-center flex items-center justify-center gap-2" data-testid="compare-empty">
          Seleziona due piloti per confrontarli <ArrowRight className="h-4 w-4" />
        </p>
      ) : (
        <div className="border border-zinc-800" data-testid="compare-table">
          <div className="grid grid-cols-12 gap-2 px-5 py-4 bg-zinc-900 items-center">
            <div className="col-span-5 font-heading font-black text-lg text-yellow-400 truncate text-right">{dataA.name}</div>
            <div className="col-span-2 text-center text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">VS</div>
            <div className="col-span-5 font-heading font-black text-lg text-cyan-400 truncate">{dataB.name}</div>
          </div>
          {rows.map((row) => {
            const w = winner(row);
            return (
              <div key={row.key} className="grid grid-cols-12 gap-2 px-5 py-4 items-center border-t border-zinc-800" data-testid={`compare-row-${row.key}`}>
                <div className={`col-span-5 text-right font-display font-bold text-xl tabular-nums ${w === "a" ? "text-yellow-400" : "text-zinc-300"}`}>
                  {row.get(dataA)}
                </div>
                <div className="col-span-2 text-center text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500">{row.label}</div>
                <div className={`col-span-5 text-left font-display font-bold text-xl tabular-nums ${w === "b" ? "text-cyan-400" : "text-zinc-300"}`}>
                  {row.get(dataB)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
